import type {MantineColor, MantineSize} from '@mantine/core';
import React from 'react';

import BenchlingIcon, {type BenchlingIconName} from 'ui-library/icons/BenchlingIcon';

import StatusPill from './StatusPill';

/**
 * Canonical union for the shared validation badge. Structurally identical to
 * `InputStatus` (new-prediction/newPredictionState), so that call site passes its
 * own type with no cast and `lib/` stays independent of feature state.
 */
export type ValidationStatus = 'empty' | 'incomplete' | 'invalid' | 'ready';

interface ValidationBadgeConfig {
  color: MantineColor;
  icon: BenchlingIconName;
  label: string;
}

const VALIDATION_BADGE_CONFIG: Record<ValidationStatus, ValidationBadgeConfig> = {
  empty: {color: 'yellow', icon: 'exclamation-triangle', label: 'Empty'},
  incomplete: {color: 'yellow', icon: 'exclamation-triangle', label: 'Incomplete'},
  invalid: {color: 'red', icon: 'circle-xmark', label: 'Invalid'},
  ready: {color: 'green', icon: 'check-circle', label: 'Ready'},
};

interface ValidationBadgeProps {
  status: ValidationStatus;
  // Defaults to the compact `xs` size to match the design's ~11px validation pills.
  size?: MantineSize;
}

export default function ValidationBadge({status, size = 'xs'}: ValidationBadgeProps): JSX.Element {
  const config = VALIDATION_BADGE_CONFIG[status];

  return (
    <StatusPill
      color={config.color}
      leftSection={<BenchlingIcon aria-hidden={true} icon={config.icon} />}
      size={size}
    >
      {config.label}
    </StatusPill>
  );
}
